import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Alert,
  Box
} from '@mui/material';

// 색상 변환 헬퍼 함수
// Integer (R + G*256 + B*256*256) -> Hex (#RRGGBB)
const intToHex = (intVal) => {
  if (intVal === null || intVal === undefined || isNaN(intVal)) return '#000000';
  
  // intVal = R + (G << 8) + (B << 16)
  const r = intVal & 255;
  const g = (intVal >> 8) & 255;
  const b = (intVal >> 16) & 255;
  
  // Hex String (#RRGGBB)
  const toHex = (c) => {
    const hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Hex (#RRGGBB) -> Integer (R + G*256 + B*256*256)
const hexToInt = (hex) => {
  if (!hex || !hex.startsWith('#')) return 0;
  
  // hex = #RRGGBB
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  
  return r + (g * 256) + (b * 65536);
};

const StatusMasterDialog = ({ 
  open, 
  onClose, 
  onSave, 
  data, 
  mode,
  projectId,
  group_code,
  project_code,
  existingData = []
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState('');

  // 다이얼로그가 열릴 때 데이터 초기화
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && data) {
        // 편집 모드일 때 기존 데이터로 초기화
        setFormData({...data});
      } else {
        // 추가 모드일 때 기본값으로 초기화
        setFormData({
          statusId: '',
          statusName: '',
          mchColor: 0, // 기본값 검정
          mchBlink: 0,
          titleColor: 0,
          textColor: 0,
          textBkColor: 0,
          soundId: 0
        });
      }
      
      setErrors({});
      setAlertMessage('');
    }
  }, [open, data, mode]);

  // 입력 필드 변경 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // 상태 ID 중복 검사 (추가 모드에서만)
    if (name === 'statusId' && mode === 'add' && value) {
      const isDuplicate = existingData.some(item => String(item.statusId) === String(value));
      if (isDuplicate) {
        setErrors(prev => ({
          ...prev,
          statusId: '이미 존재하는 상태 ID입니다.'
        }));
      }
    }
  };

  // 색상 변경 핸들러
  const handleColorChange = (name, hexValue) => {
    const intValue = hexToInt(hexValue);
    setFormData(prev => ({
      ...prev,
      [name]: intValue
    }));
  };

  // 유효성 검사
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.statusId && formData.statusId !== 0) {
      newErrors.statusId = '상태 ID를 입력하세요';
    } else if (mode === 'add') {
      const isDuplicate = existingData.some(item => String(item.statusId) === String(formData.statusId));
      if (isDuplicate) {
        newErrors.statusId = '이미 존재하는 상태 ID입니다.';
      }
    }
    
    if (!formData.statusName) {
      newErrors.statusName = '상태명을 입력하세요';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 저장 핸들러
  const handleSave = () => {
    if (validateForm()) {
      const submitData = {...formData};
      
      // 숫자 필드 변환
      submitData.statusId = Number(submitData.statusId);
      submitData.mchBlink = Number(submitData.mchBlink);
      submitData.soundId = Number(submitData.soundId);
      
      onSave(submitData, mode);
    } else {
      setAlertMessage('필수 항목을 확인하세요.');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
        {mode === 'add' ? '상태 유형 추가' : '상태 유형 편집'}
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: 3 }}>
        {alertMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {alertMessage}
          </Alert>
        )}
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="statusId"
              label="상태 ID"
              type="number"
              fullWidth
              value={formData.statusId ?? ''}
              onChange={handleChange}
              error={!!errors.statusId}
              helperText={errors.statusId}
              disabled={mode === 'edit'} // 편집 시 ID 변경 불가
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="statusName"
              label="상태명"
              fullWidth
              value={formData.statusName ?? ''}
              onChange={handleChange}
              error={!!errors.statusName}
              helperText={errors.statusName}
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                name="mchColor"
                label="장비 색상 (Integer)"
                fullWidth
                value={formData.mchColor ?? 0}
                InputProps={{
                  readOnly: true,
                }}
              />
              <input
                type="color"
                value={intToHex(formData.mchColor)}
                onChange={(e) => handleColorChange('mchColor', e.target.value)}
                style={{ width: '50px', height: '50px', border: 'none', cursor: 'pointer' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
             <FormControl fullWidth>
              <InputLabel id="mch-blink-label">장비 점멸</InputLabel>
              <Select
                labelId="mch-blink-label"
                name="mchBlink"
                value={formData.mchBlink ?? 0}
                onChange={handleChange}
                label="장비 점멸"
              >
                <MenuItem value={0}>0 : 미사용</MenuItem>
                <MenuItem value={1}>1 : 점멸</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                name="titleColor"
                label="말풍선 제목 색상"
                fullWidth
                value={formData.titleColor ?? 0}
                InputProps={{
                  readOnly: true,
                }}
              />
              <input
                type="color"
                value={intToHex(formData.titleColor)}
                onChange={(e) => handleColorChange('titleColor', e.target.value)}
                style={{ width: '50px', height: '50px', border: 'none', cursor: 'pointer' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                name="textColor"
                label="말풍선 내용 색상"
                fullWidth
                value={formData.textColor ?? 0}
                InputProps={{
                  readOnly: true,
                }}
              />
              <input
                type="color"
                value={intToHex(formData.textColor)}
                onChange={(e) => handleColorChange('textColor', e.target.value)}
                style={{ width: '50px', height: '50px', border: 'none', cursor: 'pointer' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                name="textBkColor"
                label="내용 배경색"
                fullWidth
                value={formData.textBkColor ?? 0}
                InputProps={{
                  readOnly: true,
                }}
              />
              <input
                type="color"
                value={intToHex(formData.textBkColor)}
                onChange={(e) => handleColorChange('textBkColor', e.target.value)}
                style={{ width: '50px', height: '50px', border: 'none', cursor: 'pointer' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="soundId"
              label="소리 ID (미구현)"
              type="number"
              fullWidth
              value={formData.soundId ?? 0}
              onChange={handleChange}
            />
          </Grid>
          
        </Grid>
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleSave} variant="contained" color="primary">
          저장
        </Button>
        <Button onClick={onClose} color="inherit">
          취소
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusMasterDialog;

